import React, { FC } from 'react'
import { useDispatch } from 'react-redux'
import { isPresent } from 'ts-extras'
import { Actions } from '~/actions'
import { useTranslations } from '~/components/Locale'
import type { Product, StagedUpsell } from '~/types'
import { UpsellCard } from './UpsellCard'

export interface ProductUpsellsProps {
	product: Product
	upsells: StagedUpsell[]
}

export const ProductUpsells: FC<ProductUpsellsProps> = ({ product, upsells }) => {
	const dispatch = useDispatch()
	const t = useTranslations('react')

	const productUpsells = extractProductUpsells(product)
	if (!productUpsells) return null
	const nFree = extractUpsellDiscount(product)

	const inCartUpsells = upsells.filter(item => productUpsells.some(u => u.variants[0].id === item.id))
	const discountedInCartIds = nFree ? inCartUpsells.slice(0, nFree).map(u => u.id) : []

	return (
		<ul className="list grid grid-cols-3 gap-4 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-4">
			{productUpsells.map(upsell => {
				const variant = upsell.variants[0]
				const inCart = upsells.some(item => item.id === variant.id)
				let discount: number | undefined
				if (nFree && nFree > 0) {
					if (inCartUpsells.length < nFree) {
						discount = parseFloat(variant.price.amount)
					} else if (inCart && discountedInCartIds.includes(variant.id)) {
						discount = parseFloat(variant.price.amount)
					}
				}

				return (
					<UpsellCard
						discount={discount}
						inCart={inCart}
						product={upsell}
						key={upsell.id}
						className="bg-gray-50"
						onClick={_ => dispatch(inCart ? Actions.removeUpsell(variant.id) : Actions.addUpsell(variant))}
						title={inCart ? t?.actions.add_to_cart : 'Remove'}
					/>
				)
			})}
		</ul>
	)
}

function extractUpsellDiscount(product: Product) {
	const upsell = product.upsells?.reference
	if (upsell?.__typename !== 'Metaobject') return null
	if (upsell.first_free_variants?.value) {
		const upsells = JSON.parse(upsell.first_free_variants.value) as string[]
		if (!upsells.some(id => product.variants.some(v => v.id === id))) return null
		return Number.parseInt(upsell.first_free_amount?.value ?? '0', 10)
	} else {
		return Number.parseInt(upsell.first_free_amount?.value ?? '0', 10)
	}
	return null
}

export function extractProductUpsells(product: Product) {
	const upsells = product.upsells?.reference
	if (upsells?.__typename !== 'Metaobject') return null
	const productRefs = upsells.products?.references
		?.map(u => (u?.__typename === 'Product' ? u : null))
		.filter(isPresent)
	return productRefs?.length && productRefs.length > 0 ? productRefs : null
}
